<template>
  <div
    v-if="display === 'new_user'"
    class="text-center"
  >
    <Heading
      type="h1"
      variant="h3"
    >
      {{ $t('hooray') }} 🎉
    </Heading>
    <p class="mt-3">
      {{ $t('experience_creation_description') }}
    </p>
  </div>
  <div
    v-else-if="display === 'existing_user'"
    class="text-center"
  >
    <Heading
      type="h1"
      variant="h3"
    >
      {{ $t('magic_link_email_sent_title') }}
    </Heading>
    <Markdown
      as="p"
      class="mt-3"
      :text="$t('magic_link_email_sent_description').replace(':userEmail', data.email) + ' ✨'"
    />
  </div>
  <Form
    v-else
    :data="data"
    endpoint="attempt"
    hide-submit
    :before-submit="checkHoneypot"
    :ui="ui?.form"
    @success="onSuccess"
  >
    <div
      class="space-y-4"
      :class="{ 'md:flex md:space-x-4 md:space-y-0': inline }"
    >
      <InputGroup
        name="email"
        :class="{ 'md:grow': inline }"
      >
        <TextInput
          v-model="data.email"
          type="email"
          name="email"
          :placeholder="action === 'register' ? `${$t('you_email_com')}@email.com` : $t('email_address')"
          auto-focus
          :ui="ui?.input"
        />
      </InputGroup>

      <input
        v-model="data.website"
        type="text"
        name="website"
        autocomplete="off"
        tabindex="-1"
        aria-hidden="true"
        class="absolute -left-[9999px] h-0 w-0 overflow-hidden opacity-0"
      />
      <div>
        <Button
          type="submit"
          :color="buttonColor"
          full-width
          :ui="ui?.button"
        >
          {{ buttonLabel || (action === 'login' ? $t('sign_in') : $t('create_account')) }}
        </Button>
      </div>
    </div>
  </Form>
</template>

<script setup lang="ts">
import type { ClassValue } from 'clsx'
import type { SignupSource } from '~/composables/useSignup'
import type { User } from '~/types/user'
import type { ButtonColor } from '../wk/elements/Button.vue'

export type LoginOrRegisterResponse = {
  new_user: boolean
  redirect_to: string | null
  user: User | null
}

const props = withDefaults(
  defineProps<{
    action?: 'login' | 'register'
    source?: SignupSource
    collectionHashid?: string
    partnerSlug?: string
    redirectTo?: string
    buttonColor?: ButtonColor
    buttonLabel?: string
    inline?: boolean
    ui?: Partial<{
      base: ClassValue
      form: ClassValue
      input: ClassValue
      button: ClassValue
    }>
  }>(),
  {
    action: 'register',
    ui: {}
  }
)

const emit = defineEmits(['success'])

const display = ref<'form' | 'new_user' | 'existing_user'>('form')
const { locale } = useI18n()
watch(locale, () => (data.value.language = locale.value))

const data = ref({
  email: '',
  language: locale.value,
  source: props.source,
  collection_hashid: props.collectionHashid,
  partner_slug: props.partnerSlug,
  redirect_to: props.redirectTo,
  // Hidden from people; sent so the API can turn away bots that skip this page
  website: ''
})

// Pages can change where they want to return to while the form is showing
watch(() => props.redirectTo, (redirectTo) => {
  data.value.redirect_to = redirectTo
})

// Honeypot guard: if a bot has filled the hidden field, fake success without hitting the API
const checkHoneypot = async (): Promise<boolean> => {
  if (data.value.email && data.value.website) {
    await new Promise(resolve => setTimeout(resolve, 1500))
    display.value = 'existing_user'
    return false
  }

  return true
}

// Success handler
const onSuccess = async ({ new_user, redirect_to, user }: LoginOrRegisterResponse) => {
  display.value = new_user ? 'new_user' : 'existing_user'
  emit('success', { new_user, redirect_to, user })

  localStorage.setItem('show_modal', new_user ? 'new_user' : 'existing_user')

  if (new_user) {
    const { user: authUser } = useAuth()
    authUser.value = user

    if (redirect_to) {
      await navigateTo(redirect_to, { replace: true })
    }
  }
}
</script>
